// assets/buy-singles.js
// Curio Cards — Buy Singles widget
// Deployed to Shopify assets, loaded by buy-singles.liquid

(function () {
  'use strict';

  // Labels for non-regular printings (mirrors FINISHES in scripts/buy-form/config.js).
  const FINISH_LABELS = { 'reverse-holo': 'Reverse Holo', 'foil': 'Foil' };

  // ── State ──────────────────────────────────────────────────────────────────
  let cfg = {};
  let state = {
    game:       'pokemon',
    q:          '',
    set:        '',
    rarity:     '',
    sort:       'price_desc',
    page:       1,
    loading:    false,
    cards:      [],
    total:      0,
    totalPages: 1,
    sets:       [],
    rarities:   [],
    inventory:  {},      // variantId → quantityAvailable
    basket:     [],      // array of card objects in multi-add queue
    trendCards: [],      // top 5 trending cards
    drawer:     null,    // currently open quick-view card
  };

  // ── DOM helpers ────────────────────────────────────────────────────────────
  function qs(sel, root) { return (root || document).querySelector(sel); }

  // ── Sparkline scale + state (mirrors scripts/buy-form/sell-price.js) ─────────
  // Floor the visual range so tiny moves don't stretch to full height.
  function sparklineScale(prices, minRangePct = 0.10) {
    const min = Math.min(...prices), max = Math.max(...prices);
    const mid = (min + max) / 2;
    const range = Math.max(max - min, mid * minRangePct) || 1;
    return { plotMin: mid - range / 2, range };
  }

  // 'none' | 'building' (all-identical & < minDays) | 'trend'
  function sparklineState(nmHistory, minDays = 7) {
    const prices = (nmHistory || []).map(h => h.nmEu ?? h.nm).filter(n => n > 0);
    if (prices.length < 2) return { state: 'none', days: prices.length };
    const distinct = new Set(prices).size;
    if (distinct < 2 && prices.length < minDays) return { state: 'building', days: prices.length };
    return { state: 'trend', days: prices.length };
  }

  // ── Sparkline SVG ──────────────────────────────────────────────────────────
  function buildSparkline(nmHistory, width, height) {
    const prices = (nmHistory || []).map(h => h.nmEu ?? h.nm).filter(n => n > 0);
    if (prices.length < 2) return null;
    const min = Math.min(...prices), max = Math.max(...prices);
    const flat = (max - min) === 0;
    const { plotMin, range } = sparklineScale(prices);
    const pts = prices.map((p, i) => ({
      x: (i / (prices.length - 1)) * width,
      y: height - ((p - plotMin) / range) * (height - 2) - 1,
    }));
    const first = prices[0], last = prices[prices.length - 1];
    const pctChange = (last - first) / first;
    const color = Math.abs(pctChange) < 0.02
      ? 'rgba(255,255,255,0.25)'
      : pctChange > 0 ? '#4ade80' : '#f87171';

    const polyPoints = pts.map(p => `${p.x},${p.y}`).join(' ');
    const fillPoints = `0,${height} ${polyPoints} ${width},${height}`;

    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', String(height));
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.display = 'block';

    const defs = document.createElementNS(svgNS, 'defs');
    const grad = document.createElementNS(svgNS, 'linearGradient');
    const gid  = `sg-${Math.random().toString(36).slice(2)}`;
    grad.setAttribute('id', gid);
    grad.setAttribute('x1', '0'); grad.setAttribute('y1', '0');
    grad.setAttribute('x2', '0'); grad.setAttribute('y2', '1');

    const stop1 = document.createElementNS(svgNS, 'stop');
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('stop-color', color);
    stop1.setAttribute('stop-opacity', '0.15');
    const stop2 = document.createElementNS(svgNS, 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('stop-color', color);
    stop2.setAttribute('stop-opacity', '0');
    grad.appendChild(stop1); grad.appendChild(stop2);
    defs.appendChild(grad);
    svg.appendChild(defs);

    const fill = document.createElementNS(svgNS, 'polygon');
    fill.setAttribute('points', fillPoints);
    fill.setAttribute('fill', `url(#${gid})`);

    const line = document.createElementNS(svgNS, 'polyline');
    line.setAttribute('points', polyPoints);
    line.setAttribute('fill', 'none');
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', '1.8');
    line.setAttribute('stroke-linecap', 'round');
    line.setAttribute('stroke-linejoin', 'round');

    const dot = document.createElementNS(svgNS, 'circle');
    dot.setAttribute('cx', String(pts[pts.length - 1].x));
    dot.setAttribute('cy', String(pts[pts.length - 1].y));
    dot.setAttribute('r', '2.5');
    dot.setAttribute('fill', color);

    if (!flat) svg.appendChild(fill);
    svg.appendChild(line);
    svg.appendChild(dot);
    return { svg, color, pctChange };
  }

  // "Building history · Nd" placeholder shown when a card has too little movement
  // yet to draw a meaningful trend (all-identical values, < 7 days of data).
  function buildSparklineBuilding(days, height) {
    const el = document.createElement('div');
    el.className = 'bs-spark-building';
    if (height) el.style.height = `${height}px`;
    el.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'
      + `<span>Building history · ${days}d</span>`;
    return el;
  }

  // ── Trend label ────────────────────────────────────────────────────────────
  function trendLabel(nmHistory) {
    const prices = (nmHistory || []).map(h => h.nmEu ?? h.nm).filter(n => n > 0);
    if (prices.length < 2) return '';
    const window = prices.slice(-7);
    if (window.length < 2) return '';
    const pct = (window[window.length - 1] - window[0]) / window[0];
    if (Math.abs(pct) < 0.02) return '';
    const sign = pct > 0 ? '↑' : '↓';
    return `${sign} ${Math.abs(Math.round(pct * 100))}% · ${window.length}d`;
  }

  // ── Numeric variant ID from GID ────────────────────────────────────────────
  function numericId(gid) {
    return gid ? gid.split('/').pop() : null;
  }

  // ── API calls ──────────────────────────────────────────────────────────────
  async function fetchSingles(params = {}) {
    const p = { game: state.game, q: state.q, set: state.set, rarity: state.rarity, sort: state.sort, page: state.page, ...params };
    const qstr = new URLSearchParams(Object.entries(p).filter(([, v]) => v !== '' && v != null)).toString();
    const res = await fetch(`${cfg.apiUrl}/api/singles/list?${qstr}`);
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  }

  async function fetchTrending() {
    const res = await fetch(`${cfg.apiUrl}/api/singles/list?sort=movers_desc&limit=5`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.cards || [];
  }

  async function fetchInventory(variantIds) {
    if (!variantIds.length) return {};
    try {
      const res = await fetch(`${cfg.apiUrl}/api/singles/inventory?ids=${encodeURIComponent(variantIds.join(','))}`);
      if (!res.ok) { console.warn('[BuySingles] Inventory fetch failed:', res.status); return {}; }
      return res.json();
    } catch (e) {
      console.warn('[BuySingles] Inventory fetch error:', e.message);
      return {};
    }
  }

  async function addToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      let msg = `Cart add failed (${res.status})`;
      try { const body = await res.json(); msg = body.description || body.message || msg; } catch (_) {}
      throw new Error(msg);
    }
    const data = await res.json();

    // Notify the rest of the theme that the cart changed so the header cart
    // badge (and any other PUB_SUB_EVENTS.cartUpdate subscribers) update
    // immediately. Without this the badge only refreshes on a full page reload.
    // The /cart/add.js response is a CartItem (no item_count), so subscribers
    // such as the header fall back to fetching /cart.js for the real total.
    try {
      const cartEvt = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
      const pub = (typeof publish === 'function') ? publish : window.publish;
      if (typeof pub === 'function') {
        pub(cartEvt, { source: 'buy-singles', cartData: data });
      }
    } catch (e) {
      console.warn('[BuySingles] cartUpdate publish failed:', e.message);
    }

    // Fetch fresh section HTML and open the cart drawer
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionsRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
        if (sectionsRes.ok) {
          const sections = await sectionsRes.json();
          cartDrawer.renderContents({ sections });
          cartDrawer.classList.remove('is-empty'); // mirrors product-form.js finally block line 104
        }
      }
    } catch (e) {
      console.warn('[BuySingles] Cart drawer update failed:', e.message);
    }

    return data;
  }

  // ── Price drop detection (7-day window) ───────────────────────────────────
  function isPriceDrop(nmHistory) {
    const prices = (nmHistory || []).map(h => h.nmEu ?? h.nm).filter(n => n > 0);
    if (prices.length < 2) return false;
    const w = prices.slice(-7);
    if (w.length < 2) return false;
    return (w[w.length - 1] - w[0]) / w[0] < -0.10;
  }

  // ── Game selector ──────────────────────────────────────────────────────────
  function renderGameSelector() {
    const wrap = qs('#bs-game-selector');
    if (!wrap) return;
    wrap.innerHTML = '';
    const games = [
      { id: 'pokemon',   label: 'Pokémon',   color: '#FFCC00', rgb: '255,204,0'  },
      { id: 'riftbound', label: 'Riftbound', color: '#E87B2A', rgb: '232,123,42' },
    ];
    games.forEach(g => {
      const tile = document.createElement('button');
      tile.className = 'bs-game-tile' + (state.game === g.id ? ' is-active' : '');
      tile.dataset.game = g.id;
      tile.style.setProperty('--tile-color', g.color);
      tile.style.setProperty('--tile-rgb', g.rgb);

      const glow = document.createElement('div');
      glow.className = 'bs-game-tile-glow';
      tile.appendChild(glow);

      const shimmer = document.createElement('div');
      shimmer.className = 'bs-game-tile-shimmer';
      tile.appendChild(shimmer);

      const logoUrl = cfg.gameLogos && cfg.gameLogos[g.id];
      if (logoUrl) {
        const img = document.createElement('img');
        img.className = 'bs-game-tile-logo';
        img.src = logoUrl; img.alt = g.label; img.loading = 'eager';
        tile.appendChild(img);
      } else {
        const wordmark = document.createElement('span');
        wordmark.className = 'bs-game-tile-wordmark';
        wordmark.textContent = g.label;
        tile.appendChild(wordmark);
      }

      const name = document.createElement('span');
      name.className = 'bs-game-tile-name';
      name.textContent = g.label;
      tile.appendChild(name);

      tile.addEventListener('click', () => {
        state.game = g.id;
        state.page = 1;
        state.set = ''; state.rarity = '';
        renderGameSelector();
        loadAndRender();
      });
      wrap.appendChild(tile);
    });
  }

  // ── Filters ────────────────────────────────────────────────────────────────
  function renderFilters() {
    const setEl    = qs('#bs-set-filter');
    const rarityEl = qs('#bs-rarity-filter');
    if (setEl) {
      setEl.innerHTML = '';
      const defaultOpt = new Option('All sets', '');
      setEl.appendChild(defaultOpt);
      state.sets.forEach(s => {
        const opt = new Option(s, s);
        if (state.set === s) opt.selected = true;
        setEl.appendChild(opt);
      });
    }
    if (rarityEl) {
      rarityEl.innerHTML = '';
      const defaultOpt = new Option('All rarities', '');
      rarityEl.appendChild(defaultOpt);
      state.rarities.forEach(r => {
        const opt = new Option(r, r);
        if (state.rarity === r) opt.selected = true;
        rarityEl.appendChild(opt);
      });
    }
  }

  // ── Card tile ──────────────────────────────────────────────────────────────
  function cardInBasket(card) { return state.basket.some(c => c.id === card.id); }

  function renderCard(card) {
    const inv        = state.inventory[card.shopifyVariantId];
    const outOfStock = inv != null && inv === 0;
    const onlyOne    = inv === 1;
    const inBasket   = cardInBasket(card);
    const drop       = isPriceDrop(card.nmHistory);

    const tile = document.createElement('div');
    tile.className = 'bs-card-tile' + (outOfStock ? ' out-of-stock' : '');
    tile.dataset.cardId = card.id;

    // Image area
    const imgWrap = document.createElement('div');
    imgWrap.className = 'bs-card-img-wrap';
    if (card.imageUrl) {
      const img = document.createElement('img');
      img.src = card.imageUrl; img.alt = card.name; img.loading = 'lazy';
      img.addEventListener('click', () => openDrawer(card));
      imgWrap.appendChild(img);
    }
    if (drop) {
      const badge = document.createElement('span');
      badge.className = 'bs-badge bs-badge-drop'; badge.textContent = 'PRICE DROP';
      imgWrap.appendChild(badge);
    }
    if (onlyOne) {
      const badge = document.createElement('span');
      badge.className = 'bs-badge bs-badge-last'; badge.textContent = 'ONLY 1 LEFT';
      imgWrap.appendChild(badge);
    }
    const nmBadge = document.createElement('span');
    nmBadge.className = 'bs-badge bs-badge-nm'; nmBadge.textContent = 'NM';
    imgWrap.appendChild(nmBadge);
    const toggle = document.createElement('button');
    toggle.className = 'bs-multi-toggle' + (inBasket ? ' in-basket' : '');
    toggle.innerHTML = inBasket ? '✓' : '+';
    toggle.title = inBasket ? 'Remove from queue' : 'Add to queue';
    toggle.addEventListener('click', (e) => { e.stopPropagation(); toggleBasket(card); });
    imgWrap.appendChild(toggle);
    tile.appendChild(imgWrap);

    // Info area
    const info = document.createElement('div');
    info.className = 'bs-card-info';

    const name = document.createElement('div');
    name.className = 'bs-card-name'; name.textContent = card.name;
    info.appendChild(name);

    const meta = document.createElement('div');
    meta.className = 'bs-card-meta';
    meta.textContent = [card.set, card.number].filter(Boolean).join(' · ');
    info.appendChild(meta);

    if (card.rarity) {
      const rarityBadge = document.createElement('span');
      rarityBadge.className = 'bs-rarity-badge';
      rarityBadge.textContent = card.rarity;
      info.appendChild(rarityBadge);
    }
    // Reverse holo / foil printings are listed as their own products; the tile must
    // say so or it looks identical to the regular printing beside it.
    if (card.finish) {
      const finishBadge = document.createElement('span');
      finishBadge.className = 'bs-rarity-badge bs-finish-badge';
      finishBadge.textContent = FINISH_LABELS[card.finish] || card.finish;
      info.appendChild(finishBadge);
    }

    // Full-width sparkline (Option B: gradient fill, own row)
    const sparkWrap = document.createElement('div');
    sparkWrap.className = 'bs-sparkline-wrap';
    const sState = sparklineState(card.nmHistory);
    const built = sState.state === 'trend' ? buildSparkline(card.nmHistory, 200, 40) : null;
    if (sState.state === 'building') sparkWrap.appendChild(buildSparklineBuilding(sState.days, 40));
    else if (built) sparkWrap.appendChild(built.svg);
    info.appendChild(sparkWrap);

    // Price + trend label row
    const priceRow = document.createElement('div');
    priceRow.className = 'bs-price-row';
    const priceEl = document.createElement('span');
    priceEl.className = 'bs-price';
    priceEl.textContent = card.sellPrice ? `£${card.sellPrice.toFixed(2)}` : '—';
    const label = trendLabel(card.nmHistory);
    const labelEl = document.createElement('span');
    labelEl.className = 'bs-trend-label';
    if (label) { labelEl.textContent = label; labelEl.style.color = built?.color ?? 'rgba(255,255,255,0.4)'; }
    priceRow.appendChild(priceEl);
    priceRow.appendChild(labelEl);
    info.appendChild(priceRow);

    // Add to cart CTA
    const cta = document.createElement('button');
    cta.className = 'bs-add-btn' + (outOfStock ? ' disabled' : '');
    cta.disabled = outOfStock;
    cta.textContent = outOfStock ? 'SOLD OUT' : inBasket ? 'ADDED ✓' : 'ADD TO CART';
    if (!outOfStock) {
      cta.addEventListener('click', async () => {
        const nid = numericId(card.shopifyVariantId);
        if (!nid) return;
        cta.textContent = '…'; cta.disabled = true;
        try {
          await addToCart([{ id: parseInt(nid, 10), quantity: 1 }]);
          inv--;
          if (inv > 0) {
            cta.textContent = 'ADD TO CART'; cta.disabled = false;
          } else {
            cta.textContent = 'ADDED ✓';
          }
        } catch {
          cta.textContent = 'ERROR'; cta.disabled = false;
          setTimeout(() => { cta.textContent = 'ADD TO CART'; cta.disabled = false; }, 2000);
        }
      });
    }
    info.appendChild(cta);
    tile.appendChild(info);
    return tile;
  }

  // ── Card grid ──────────────────────────────────────────────────────────────
  function renderGrid() {
    const grid = qs('#bs-card-grid');
    if (!grid) return;
    grid.innerHTML = '';
    if (!state.cards.length) {
      grid.innerHTML = '<p class="bs-empty">No cards found. Try adjusting your filters.</p>';
      return;
    }
    state.cards.forEach(card => grid.appendChild(renderCard(card)));
  }

  function renderPagination() {
    const el = qs('#bs-pagination');
    if (!el) return;
    el.innerHTML = '';
    if (!state.totalPages || state.totalPages <= 1) return;

    const goToPage = (p) => {
      state.page = p;
      loadAndRender();
      const grid = qs('#bs-card-grid');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const makeBtn = (label, page, disabled, active) => {
      const btn = document.createElement('button');
      btn.className = 'bs-page-btn' + (active ? ' active' : '');
      btn.textContent = label;
      btn.disabled = disabled;
      if (!disabled && !active) btn.addEventListener('click', () => goToPage(page));
      return btn;
    };

    // Previous
    el.appendChild(makeBtn('←', state.page - 1, state.page <= 1, false));

    // Page numbers with ellipsis
    let prev = null;
    for (let i = 1; i <= state.totalPages; i++) {
      const show = i === 1 || i === state.totalPages || Math.abs(i - state.page) <= 2;
      if (!show) continue;
      if (prev !== null && i - prev > 1) {
        const dot = document.createElement('span');
        dot.className = 'bs-page-ellipsis'; dot.textContent = '…';
        el.appendChild(dot);
      }
      el.appendChild(makeBtn(i, i, false, i === state.page));
      prev = i;
    }

    // Next
    el.appendChild(makeBtn('→', state.page + 1, state.page >= state.totalPages, false));
  }

  // ── Multi-add basket ───────────────────────────────────────────────────────
  function toggleBasket(card) {
    const idx = state.basket.findIndex(c => c.id === card.id);
    if (idx === -1) state.basket.push(card); else state.basket.splice(idx, 1);
    renderBasketTray();
    const tile = qs(`[data-card-id="${card.id}"]`);
    if (tile) tile.replaceWith(renderCard(card));
  }

  function renderBasketTray() {
    const tray = qs('#bs-basket-tray');
    if (!tray) return;
    if (!state.basket.length) { tray.classList.remove('visible'); return; }
    const total = state.basket.reduce((s, c) => s + (c.sellPrice || 0), 0);
    tray.querySelector('.bs-tray-count').textContent = `${state.basket.length} card${state.basket.length !== 1 ? 's' : ''} queued`;
    tray.querySelector('.bs-tray-total').textContent = `£${total.toFixed(2)} total`;
    tray.classList.add('visible');
  }

  async function addAllToCart() {
    const btn = qs('#bs-add-all-btn');
    const items = state.basket
      .map(c => ({ id: parseInt(numericId(c.shopifyVariantId), 10), quantity: 1 }))
      .filter(i => !isNaN(i.id));
    if (!items.length || !btn) return;
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      await addToCart(items);
      state.basket = [];
      renderBasketTray();
      renderGrid();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      btn.textContent = 'Added ✓';
      setTimeout(() => { btn.textContent = 'Add all to cart'; btn.disabled = false; }, 2000);
    } catch {
      btn.textContent = 'Error — try again';
      btn.disabled = false;
    }
  }

  // ── Quick-view drawer ──────────────────────────────────────────────────────
  function openDrawer(card) {
    state.drawer = card;
    const overlay = qs('#bs-drawer-overlay');
    const content = qs('#bs-drawer-content');
    if (!overlay || !content) return;

    const inv      = state.inventory[card.shopifyVariantId];
    const inBasket = cardInBasket(card);
    const dState   = sparklineState(card.nmHistory);
    const built    = dState.state === 'trend' ? buildSparkline(card.nmHistory, 340, 100) : null;
    const label    = trendLabel(card.nmHistory);

    content.innerHTML = '';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'bs-drawer-close';
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', closeDrawer);
    content.appendChild(closeBtn);

    // Hero image
    const hero = document.createElement('div');
    hero.className = 'bs-drawer-hero';
    if (card.imageUrl) {
      const img = document.createElement('img');
      img.src = card.imageUrl; img.className = 'bs-drawer-img'; img.alt = card.name;
      hero.appendChild(img);
    }
    content.appendChild(hero);

    // Body
    const body = document.createElement('div');
    body.className = 'bs-drawer-body';

    const nameEl = document.createElement('div');
    nameEl.className = 'bs-drawer-name'; nameEl.textContent = card.name;
    body.appendChild(nameEl);

    const metaEl = document.createElement('div');
    metaEl.className = 'bs-drawer-meta';
    metaEl.textContent = [card.set, card.number, card.rarity, FINISH_LABELS[card.finish]].filter(Boolean).join(' · ');
    body.appendChild(metaEl);

    const condBadge = document.createElement('div');
    condBadge.className = 'bs-drawer-condition';
    condBadge.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> Near Mint (NM)';
    body.appendChild(condBadge);

    // Price chart block
    const chartWrap = document.createElement('div');
    chartWrap.className = 'bs-drawer-chart-wrap';

    const chartLabel = document.createElement('div');
    // Label with the real window: history holds up to 30 daily snapshots, but newly
    // tracked cards have fewer — never claim more days than we actually plot.
    chartLabel.className = 'bs-drawer-chart-label';
    chartLabel.textContent = dState.state === 'trend' ? `${dState.days}-day price trend` : 'Price trend';
    chartWrap.appendChild(chartLabel);

    if (built) {
      const sparkWrap = document.createElement('div');
      sparkWrap.className = 'bs-drawer-sparkline'; sparkWrap.appendChild(built.svg);
      chartWrap.appendChild(sparkWrap);
    } else if (dState.state === 'building') {
      chartWrap.appendChild(buildSparklineBuilding(dState.days, 100));
    } else {
      const noData = document.createElement('div');
      noData.style.cssText = 'height:100px;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.2);font-size:0.78rem;';
      noData.textContent = 'No price history available';
      chartWrap.appendChild(noData);
    }

    const chartFooter = document.createElement('div');
    chartFooter.className = 'bs-drawer-chart-footer';

    const priceEl = document.createElement('div');
    priceEl.className = 'bs-drawer-price';
    priceEl.textContent = card.sellPrice ? `£${card.sellPrice.toFixed(2)}` : '—';
    chartFooter.appendChild(priceEl);

    if (label && built) {
      const trendEl = document.createElement('div');
      trendEl.className = 'bs-drawer-trend';
      trendEl.textContent = label; trendEl.style.color = built.color;
      chartFooter.appendChild(trendEl);
    }
    chartWrap.appendChild(chartFooter);
    body.appendChild(chartWrap);

    // Stock
    if (inv != null) {
      const stock = document.createElement('div'); stock.className = 'bs-drawer-stock';
      stock.textContent = inv === 0 ? 'Out of stock' : inv === 1 ? 'Only 1 left in stock' : `${inv} in stock`;
      body.appendChild(stock);
    }

    // Add to cart
    const addBtn = document.createElement('button');
    addBtn.className = 'bs-drawer-add-btn';
    addBtn.disabled = inv === 0;
    addBtn.textContent = inv === 0 ? 'SOLD OUT' : inBasket ? 'ADDED ✓' : 'ADD TO CART';
    if (inv !== 0) {
      addBtn.addEventListener('click', async () => {
        const nid = numericId(card.shopifyVariantId);
        if (!nid) return;
        addBtn.disabled = true; addBtn.textContent = '…';
        try {
          await addToCart([{ id: parseInt(nid, 10), quantity: 1 }]);
          inv--;
          addBtn.textContent = inv > 0 ? 'ADD TO CART' : 'ADDED ✓';
          if (inv > 0) addBtn.disabled = false;
        } catch {
          addBtn.textContent = 'Error — try again'; addBtn.disabled = false;
        }
      });
    }
    body.appendChild(addBtn);
    content.appendChild(body);

    overlay.addEventListener('click', e => { if (e.target === overlay) closeDrawer(); });
    overlay.style.display = 'flex';
  }

  function closeDrawer() {
    const overlay = qs('#bs-drawer-overlay');
    if (overlay) overlay.style.display = 'none';
    state.drawer = null;
  }

  // ── Trending strip ─────────────────────────────────────────────────────────
  async function renderTrendingStrip() {
    const strip = qs('#bs-trending-strip');
    if (!strip) return;
    try {
      state.trendCards = await fetchTrending();
      if (!state.trendCards.length) { strip.style.display = 'none'; return; }
      const inner = qs('#bs-trending-inner');
      if (!inner) return;
      inner.innerHTML = '';
      state.trendCards.forEach(card => {
        const built = buildSparkline(card.nmHistory, 130, 36);
        const label = trendLabel(card.nmHistory);

        const item = document.createElement('div');
        item.className = 'bs-trending-item';
        item.addEventListener('click', () => openDrawer(card));

        // Portrait image area
        const imgWrap = document.createElement('div');
        imgWrap.className = 'bs-trending-img-wrap';
        if (card.imageUrl) {
          const img = document.createElement('img');
          img.src = card.imageUrl; img.className = 'bs-trending-img'; img.alt = card.name; img.loading = 'lazy';
          imgWrap.appendChild(img);
        }
        if (label && built) {
          const badge = document.createElement('span');
          badge.className = 'bs-trending-badge';
          badge.textContent = label;
          badge.style.color = built.color.startsWith('rgba') ? '#fff' : built.color === '#4ade80' ? '#052e16' : '#1f0000';
          badge.style.background = built.color === '#4ade80' ? 'rgba(74,222,128,0.9)' : built.color === '#f87171' ? 'rgba(248,113,113,0.9)' : 'rgba(255,255,255,0.15)';
          imgWrap.appendChild(badge);
        }
        item.appendChild(imgWrap);

        // Body
        const body = document.createElement('div');
        body.className = 'bs-trending-body';

        const nameEl = document.createElement('div');
        nameEl.className = 'bs-trending-name'; nameEl.textContent = card.name;
        body.appendChild(nameEl);

        const sparkDiv = document.createElement('div');
        sparkDiv.className = 'bs-trending-spark';
        if (built) sparkDiv.appendChild(built.svg);
        body.appendChild(sparkDiv);

        const footer = document.createElement('div');
        footer.className = 'bs-trending-footer';
        const priceEl = document.createElement('span');
        priceEl.className = 'bs-trending-price';
        priceEl.textContent = card.sellPrice ? `£${card.sellPrice.toFixed(2)}` : '—';
        footer.appendChild(priceEl);
        if (label && built) {
          const pctEl = document.createElement('span');
          pctEl.className = 'bs-trending-pct';
          pctEl.textContent = label;
          pctEl.style.color = built.color;
          footer.appendChild(pctEl);
        }
        body.appendChild(footer);
        item.appendChild(body);
        inner.appendChild(item);
      });
    } catch (e) {
      console.warn('[BuySingles] Trending strip failed:', e.message);
    }
  }

  // ── Main load + render ─────────────────────────────────────────────────────
  async function loadAndRender(append = false) {
    if (state.loading) return;
    state.loading = true;
    const grid = qs('#bs-card-grid');
    if (grid && !append) grid.innerHTML = '<div class="bs-spinner"></div>';

    try {
      const data = await fetchSingles();
      if (append) {
        state.cards = [...state.cards, ...data.cards];
      } else {
        state.cards      = data.cards;
        state.total      = data.total;
        state.totalPages = data.totalPages || Math.ceil((data.total || 0) / 24);
        state.sets       = data.sets || [];
        state.rarities   = data.rarities || [];
        renderFilters();
      }

      // Batch fetch Shopify inventory for all cards
      const variantIds = state.cards.map(c => c.shopifyVariantId).filter(Boolean);
      const inv = await fetchInventory(variantIds);
      state.inventory = append ? { ...state.inventory, ...inv } : inv;

      renderGrid();
      renderPagination();
    } catch (e) {
      if (grid) grid.innerHTML = `<p class="bs-empty">Failed to load cards: ${e.message}</p>`;
    } finally {
      state.loading = false;
    }
  }

  // ── Wire up filter controls ────────────────────────────────────────────────
  function wireFilters() {
    const searchEl  = qs('#bs-search');
    const clearBtn  = qs('#bs-search-clear');
    const syncClear = () => {
      if (clearBtn) clearBtn.style.display = searchEl && searchEl.value ? 'flex' : 'none';
    };
    if (searchEl) {
      let t;
      searchEl.addEventListener('input', () => {
        syncClear();
        clearTimeout(t);
        t = setTimeout(() => { state.q = searchEl.value; state.page = 1; loadAndRender(); }, 350);
      });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (searchEl) { searchEl.value = ''; searchEl.focus(); }
        clearBtn.style.display = 'none';
        state.q = ''; state.page = 1;
        loadAndRender();
      });
    }
    const sortEl = qs('#bs-sort');
    if (sortEl) sortEl.addEventListener('change', () => { state.sort = sortEl.value; state.page = 1; loadAndRender(); });
    const setEl  = qs('#bs-set-filter');
    if (setEl)  setEl.addEventListener('change',  () => { state.set = setEl.value; state.page = 1; loadAndRender(); });
    const rarEl  = qs('#bs-rarity-filter');
    if (rarEl)  rarEl.addEventListener('change',  () => { state.rarity = rarEl.value; state.page = 1; loadAndRender(); });

    const addAllBtn = qs('#bs-add-all-btn');
    if (addAllBtn) addAllBtn.addEventListener('click', addAllToCart);
  }

  // ── Public API ─────────────────────────────────────────────────────────────
  window.CurioBuySingles = {
    init(config) {
      cfg = config;
      renderGameSelector();
      wireFilters();
      renderBasketTray();
      renderTrendingStrip();
      loadAndRender();
    },
  };
})();
